import { useMemo, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

/**
 * Isometric rig figure for the web proposal. Same geometry as RigIsoPreview (boxes in metres:
 * [x, z, sx, sz, floor0, floors, color, kind, module_type_id]) but:
 *  - the rig assembles itself: modules drop in one by one (CSS keyframes, staggered)
 *  - `highlight` (module_type_id) dims every other module – hover the legend to explore
 *  - `floor` shows one storey at a time (null = all)
 *  - gentle 3D tilt following the pointer (spring, off for reduced motion / touch)
 */
const COS30 = Math.cos(Math.PI / 6);
const SIN30 = 0.5;

const shade = (hex, f) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "#d8d2c4");
  if (!m) return hex;
  const c = m.slice(1).map((h) => Math.max(0, Math.min(255, Math.round(parseInt(h, 16) * f))));
  return `#${c.map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};

export const ProposalRigFigure = ({ preview, className, height = 300, highlight = null, floor = null, animate = true, tilt = true, tone = "dark", testId = "proposal-rig-figure" }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const [hovering, setHovering] = useState(false);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 120, damping: 18, mass: 0.6 });
  const sry = useSpring(ry, { stiffness: 120, damping: 18, mass: 0.6 });

  const { boxes, viewBox, ground } = useMemo(() => {
    if (!preview?.boxes?.length) return { boxes: [], viewBox: "0 0 1 1", ground: null };
    const fh = preview.floor_height || 3;
    const P = (x, z, y) => [(x - z) * COS30, (x + z) * SIN30 - y];
    const items = preview.boxes
      .map(([x, z, sx, sz, f0, nf, color, kind, typeId], idx) => ({ idx, x, z, sx, sz, f0, y0: f0 * fh, y1: (f0 + nf) * fh, color: color || "#d8d2c4", kind, typeId }))
      .sort((a, b) => a.y0 - b.y0 || a.x + a.z - (b.x + b.z));
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const track = (pts) => {
      for (const [px, py] of pts) {
        minX = Math.min(minX, px); maxX = Math.max(maxX, px);
        minY = Math.min(minY, py); maxY = Math.max(maxY, py);
      }
    };
    const out = items.map((b, order) => {
      const { x, z, sx, sz, y0, y1, color } = b;
      const front = [P(x, z + sz, y0), P(x + sx, z + sz, y0), P(x + sx, z + sz, y1), P(x, z + sz, y1)];
      const right = [P(x + sx, z + sz, y0), P(x + sx, z, y0), P(x + sx, z, y1), P(x + sx, z + sz, y1)];
      const top = [P(x, z, y1), P(x + sx, z, y1), P(x + sx, z + sz, y1), P(x, z + sz, y1)];
      track(front); track(right); track(top);
      const d = (pts) => `M${pts.map((p) => `${p[0].toFixed(2)},${p[1].toFixed(2)}`).join("L")}Z`;
      return { ...b, order, faces: [
        { d: d(front), fill: shade(color, 0.86), stroke: shade(color, 0.7) },
        { d: d(right), fill: shade(color, 0.74), stroke: shade(color, 0.62) },
        { d: d(top), fill: shade(color, 1.04), stroke: shade(color, 0.72) },
      ] };
    });
    const pad = 1.6;
    const w = maxX - minX + pad * 2;
    const h = maxY - minY + pad * 2 + 1.5;
    return { boxes: out, viewBox: `${(minX - pad).toFixed(2)} ${(minY - pad).toFixed(2)} ${w.toFixed(2)} ${h.toFixed(2)}`, ground: { cx: (minX + maxX) / 2, cy: maxY + 0.6, rx: w * 0.42, ry: Math.max(1.2, h * 0.08) } };
  }, [preview]);

  if (!boxes.length) return null;
  const total = boxes.length;
  const step = Math.min(28, 1800 / total);
  const enableTilt = tilt && !reduce;

  const onMove = (e) => {
    if (!enableTilt || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * 7);
    rx.set(-py * 6);
  };
  const onLeave = () => { rx.set(0); ry.set(0); setHovering(false); };

  return (
    <div ref={ref} className={cn("relative", className)} style={{ perspective: 1400 }} onMouseMove={onMove} onMouseEnter={() => setHovering(true)} onMouseLeave={onLeave} data-testid={testId} data-floor={floor ?? "all"} data-highlight={highlight || ""}>
      <motion.svg
        viewBox={viewBox}
        className="block w-full overflow-visible"
        style={{ height, rotateX: enableTilt ? srx : 0, rotateY: enableTilt ? sry : 0, transformStyle: "preserve-3d" }}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Isometrisk visning av riggen"
      >
        <defs>
          <radialGradient id="rig-ground" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={tone === "dark" ? "rgba(0,0,0,0.45)" : "rgba(31,36,70,0.18)"} />
            <stop offset="100%" stopColor="rgba(0,0,0,0)" />
          </radialGradient>
        </defs>
        {ground && <ellipse cx={ground.cx} cy={ground.cy} rx={ground.rx} ry={ground.ry} fill="url(#rig-ground)" className={cn(animate && !reduce && "rig-ground-in")} />}
        {boxes.map((b) => {
          const dim = highlight && b.typeId !== highlight;
          const hidden = floor != null && b.f0 !== floor;
          return (
            <g
              key={b.idx}
              className={cn("rig-box", animate && !reduce && "rig-box-in")}
              style={{ animationDelay: `${Math.round(b.order * step)}ms`, opacity: hidden ? 0 : dim ? 0.16 : 1, transition: "opacity 220ms ease" }}
              data-kind={b.kind}
              data-floor={b.f0}
            >
              {b.faces.map((f, i) => <path key={i} d={f.d} fill={f.fill} stroke={f.stroke} strokeWidth={0.07} strokeLinejoin="round" />)}
            </g>
          );
        })}
      </motion.svg>
      {hovering && enableTilt && <span className="sr-only">Beveg musen for å vippe modellen</span>}
    </div>
  );
};
